// The gate's DECISIONS, as pure data. No React, no fetch, no window.
//
// Authority: /ceph/projects/coordination/sdk-demos/SDK-DEMO-LOGIN-STANDARD-RECIPE.md
// §1 (Path A), §5 (conformance: "the demo PAGE itself is behind the middleware (even a
// thin launcher) — an open page that merely delegates one call is NOT compliant").
//
// ---------------------------------------------------------------------------
// THE ONE INVARIANT THIS MODULE MAKES STRUCTURALLY IMPOSSIBLE TO BREAK
// ---------------------------------------------------------------------------
// HELD-MODE MUST NOT BECOME SERVICE-MODE.
//
// The demo must run as the authenticated, scoped user or not at all. There is no
// service credential, no god-mode, no unscoped path. That is not enforced by a
// convention here — it is enforced by the TYPE: `productAccess()` is the single
// chokepoint that yields a session, it takes a `GateState`, and the ONLY variant it can
// return a session from is `authenticated`. Every other variant returns `null`.
//
// So "held" cannot silently become "allowed" by someone adding a branch somewhere else,
// because there is nowhere else: a caller with a `GateState` and no session has nothing
// to call the product with. A reviewer checks one function.
//
// `held` is deliberately NOT a kind of "authenticated with unknown user". It is a kind of
// DENIED, and it sits next to `anonymous` and `error` on purpose.

import type { ResolvedSession } from './dto';

/**
 * Why the gate is in a held state.
 *
 * Held-mode exists because the real Connect login flips in later, when Connect hands
 * Infra the config — the demo must build and deploy before that. It is a VISIBLE,
 * FAIL-CLOSED state: the page renders "awaiting login configuration" and the launcher is
 * not mounted.
 */
export type HeldReason =
  /** No Connect base URL configured yet. The flip is a values change, not a rebuild. */
  | 'connect-not-configured'
  /** Operator explicitly held this deployment. */
  | 'held-by-config';

export type GateState =
  /** The validate call is in flight. Renders a spinner; mounts nothing. */
  | { kind: 'checking' }
  /** A real, scoped user. The ONLY state that yields product access. */
  | { kind: 'authenticated'; session: ResolvedSession }
  /** No session. The page must redirect to Connect; it must not render. */
  | { kind: 'anonymous'; loginUrl: string }
  /** Login not wired yet. Visible held state. NOT a licence to call anything. */
  | { kind: 'held'; reason: HeldReason }
  /** Anything went wrong. Fail closed, show the reason. */
  | { kind: 'error'; detail: string };

/**
 * THE CHOKEPOINT. The session the demo may act as, or null.
 *
 * Every caller that needs the user's scope — the launcher's `tenant`, the prep-session
 * call, anything that reaches the product — goes through this. A `null` means "do not
 * call the product", and there is no second way to obtain a session.
 *
 * Written as an exhaustive switch rather than `state.kind === 'authenticated' ? … : null`
 * so that adding a `GateState` variant without deciding its access answer is a COMPILE
 * ERROR (`never`), not a silent `null` — or worse, a silent allow if the condition had
 * been written the other way round.
 */
export function productAccess(state: GateState): ResolvedSession | null {
  switch (state.kind) {
    case 'authenticated':
      return state.session;
    case 'checking':
    case 'anonymous':
    case 'held':
    case 'error':
      return null;
    default: {
      // UNREACHABLE while the switch is exhaustive — the `never` assignment is a compile
      // error if a variant is added without an answer here. It nonetheless RETURNS NULL
      // rather than the `never` value.
      //
      // Why bother with an unreachable branch: `return exhaustive` is a branch that, if
      // it ever did run, would hand back whatever was passed in — and a mutation test
      // showed that a fabricated session placed here survives the whole suite, because
      // nothing can reach it to prove otherwise. An unreachable branch that would grant
      // access is still a branch a reviewer has to rule out. Failing closed here costs
      // nothing and makes the function's answer "null unless authenticated", with no
      // exceptions to hold in your head. SOC2-CONTROL-STANDARDS rule 1.
      const exhaustive: never = state;
      void exhaustive;
      return null;
    }
  }
}

/** May the gated page render its contents? Only when there is a real user. */
export function mayRenderGatedContent(state: GateState): boolean {
  return productAccess(state) !== null;
}

/**
 * Should the browser be navigated to Connect right now?
 *
 * Separate from `mayRenderGatedContent` because they are different questions with
 * different wrong answers. `held` must NOT redirect — there is no configured Connect to
 * redirect to, and bouncing the browser at an unconfigured URL produces a navigation
 * failure that reads as "the demo is broken" rather than "login is not wired yet".
 */
export function redirectTarget(state: GateState): string | null {
  return state.kind === 'anonymous' ? state.loginUrl : null;
}

// ---------------------------------------------------------------------------
// THE DECISION, as a pure function
// ---------------------------------------------------------------------------

/** Everything the gate needs to decide, with no IO. */
export interface GateInputs {
  /** The operator held this deployment explicitly. */
  heldByConfig: boolean;
  /** The configured Connect origin, or null/blank when not yet provided. */
  connectBaseUrl: string | null;
  /**
   * The result of asking Connect. `null` means "not asked yet".
   *
   * Typed as the probe's own union rather than a boolean so "answered no" and "could not
   * ask" cannot be collapsed — a guard that collapses them eventually reports the wrong
   * one, and here the wrong one is a login loop against an unreachable Connect.
   */
  probe:
    | null
    | { kind: 'session'; session: ResolvedSession }
    | { kind: 'no-session'; detail: string }
    | { kind: 'unreachable'; detail: string };
  /** The login URL to use when the answer is "anonymous". */
  loginUrl: string | null;
  /**
   * The bridge's failure detail, when the callback's exchange code could not be swapped.
   *
   * WHY THIS IS AN INPUT RATHER THAN A BANNER. A failed bridge leaves no session, so
   * without this the gate reaches `no-session` → `anonymous` → redirect to Connect. That
   * is a SILENT LOGIN LOOP whenever the bridge fails persistently — an expired code
   * recovers on the next round trip, but an IP-binding mismatch or a Connect-side refusal
   * does not: Connect mints a fresh code, the bridge rejects it again, the browser
   * bounces again, and the reason is painted for a few milliseconds each time before the
   * navigation discards it.
   *
   * So a bridge failure is TERMINAL and visible. The tester sees why, and a retry is
   * their decision rather than an automatic one. Found by a flaky Playwright test whose
   * assertion raced the redirect — the flake was the symptom, the loop was the defect.
   */
  bridgeFailed: string | null;
}

/**
 * Decide the gate state. Pure, exhaustive, and ordered so held wins over everything.
 *
 * ORDER MATTERS AND IS THE WHOLE POINT:
 *
 * 1. HELD IS CHECKED FIRST, before the probe is even consulted. A held deployment has no
 *    Connect to ask, so consulting the probe first would classify it `unreachable` and
 *    render an error — and an operator seeing "Connect unreachable" on a deployment that
 *    is merely not configured yet will go looking for a network fault that does not
 *    exist.
 * 2. A MISSING CONNECT URL IS ALSO HELD, not an error. The real login flips in via a
 *    values change; until then "not configured" is the expected state, not a fault.
 * 3. `unreachable` becomes `error`, NOT `anonymous`. Bouncing to login because Connect
 *    could not be reached produces an infinite redirect: the browser leaves, Connect
 *    cannot answer, the browser comes back, and nothing in the loop ever reports why.
 *
 * At no point does any input combination yield `authenticated` without a `session`
 * object from the probe. That is the held-must-not-become-service-mode invariant, and it
 * is enforced here by construction and asserted in `gate.test.ts`.
 */
export function decideGate(inputs: GateInputs): GateState {
  if (inputs.heldByConfig) return { kind: 'held', reason: 'held-by-config' };

  const base = inputs.connectBaseUrl;
  if (typeof base !== 'string' || base.trim() === '') {
    return { kind: 'held', reason: 'connect-not-configured' };
  }

  if (inputs.probe === null) return { kind: 'checking' };

  // A bridge failure outranks everything but a session that somehow resolved anyway
  // (which would mean the user already held a valid cookie, and the spent code was
  // irrelevant). Checked before `no-session` so it cannot become a redirect.
  if (inputs.probe.kind !== 'session' && inputs.bridgeFailed) {
    return { kind: 'error', detail: `sign-in did not complete: ${inputs.bridgeFailed}` };
  }

  switch (inputs.probe.kind) {
    case 'session':
      return { kind: 'authenticated', session: inputs.probe.session };
    case 'no-session':
      // Genuinely not logged in → bounce to Connect. Without a login URL we cannot, and
      // an error naming that is better than a silent dead page.
      return inputs.loginUrl
        ? { kind: 'anonymous', loginUrl: inputs.loginUrl }
        : { kind: 'error', detail: `not signed in, and no login URL could be built` };
    case 'unreachable':
      return { kind: 'error', detail: `Connect could not be reached: ${inputs.probe.detail}` };
    default: {
      const exhaustive: never = inputs.probe;
      return exhaustive;
    }
  }
}

// ---------------------------------------------------------------------------
// Configuration + URL construction
// ---------------------------------------------------------------------------

export interface ConnectConfig {
  /**
   * Connect's base URL for TOP-LEVEL NAVIGATION (the `/login?app=` bounce), no trailing
   * slash. Absolute and cross-origin — that leg is a browser navigation, not a fetch, so
   * it needs no CORS.
   */
  connectBaseUrl: string;
  /**
   * Base path for Connect's API as the BROWSER should call it.
   *
   * SAME-ORIGIN BY DEFAULT (`/api/auth`), AND THAT IS NOT AN OPTIMISATION — IT IS THE
   * ONLY SHAPE THAT WORKS. Measured 2026-10-19 against both Connect tiers: every
   * response carries `Access-Control-Allow-Credentials: true` but **no
   * `Access-Control-Allow-Origin`, for any origin** — not this demo's, not the branding
   * row's registered `primary_url`, not Connect's own, not a nonsense one. The OPTIONS
   * preflight answers 400 for all of them. A credentialed cross-origin fetch is
   * therefore blocked by the browser before it is sent.
   *
   * So the demo's own nginx reverse-proxies this path to Connect and the browser call is
   * same-origin. That forwards THE USER'S OWN COOKIE and nothing else — the demo holds no
   * credential, so this is a transport detail, not a scope escape. It is also what makes
   * the bridge's `Set-Cookie` land in the browser, which §1 calls the critical mechanic.
   *
   * Settable to an absolute Connect URL for an environment where ACAO is fixed.
   */
  authBasePath: string;
  /** The `?app=` token and the `cookie_name_for_app` argument. Short form. */
  appKey: string;
  /** Path on THIS origin that Connect redirects back to with `?exchange_code=`. */
  callbackPath: string;
}

/** Raised for a Connect configuration the demo refuses to guess at. */
export class ConnectConfigError extends Error {
  constructor(public readonly field: string, detail: string) {
    super(`connect config: ${field} — ${detail}`);
    this.name = 'ConnectConfigError';
  }
}

/**
 * The `?app=` token for this demo.
 *
 * VERIFIED, not assumed. The recipe §4 says to confirm each SDK's token against
 * `/api/branding/<token>` before hardcoding, and warns the short form is the key minus
 * the `anshin-` prefix. Measured 2026-10-19 on the Connect DEV tier:
 *
 *     GET https://connect.dev.anshinhealth.dev/api/branding/anna-sdk
 *       -> is_fallback=false, app_key="anshin-anna-sdk",
 *          identity.primary_url="https://anna-sdk.anshin.us/"
 *
 * A 200 ALONE PROVES NOTHING and the recipe's own check is unsound as written: on PROD
 * every token returns 200 — including `ui-sdk`, `auth-sdk` and a deliberate
 * `definitely-not-an-app` — all with `is_fallback: true` and `app_key: "anshin-connect"`,
 * because v4.96.0's branding rows have not merged to prod yet. `is_fallback` is the
 * discriminator, not the status code.
 */
export const ANNA_SDK_APP_KEY = 'anna-sdk';

/**
 * The per-app session cookie name.
 *
 * Recorded for the record and for the held-state display; the BROWSER CANNOT READ IT.
 * The cookie is `HttpOnly`, so a client-side presence check is impossible — which is why
 * the gate's liveness test is a credentialed call to `/session/validate` and not
 * `document.cookie`. Cross-checked against the shipped helper:
 * `cookieNameForApp('anna-sdk')` returns exactly this.
 *
 * NOTE `anna-sdk`, NOT `anna`. `@anshinus/auth`'s `KNOWN_APP_KEYS` contains `anna` (and
 * no `*-sdk` key at all, so `isKnownAppKey('anna-sdk')` is false and advisory only).
 * Using `anna` would resolve `anshin_anna_session` — the anshin-anna PRODUCT's own
 * session cookie — and the demo would be reading and writing the product's session
 * instead of its own.
 */
export const ANNA_SDK_COOKIE = 'anshin_anna_sdk_session';

export const DEFAULT_CALLBACK_PATH = '/login/callback';
export const DEFAULT_AUTH_BASE_PATH = '/api/auth';

/**
 * Normalise a Connect base URL, or throw.
 *
 * Same rules as the product base URL: absolute `https:`, origin only, one trailing slash
 * stripped. `http:` is refused rather than upgraded — this URL is where the user's
 * credentials are entered, so a cleartext scheme in a values file is the one thing that
 * must never be silently corrected.
 */
export function normaliseConnectBaseUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) throw new ConnectConfigError('connectBaseUrl', 'empty');
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new ConnectConfigError('connectBaseUrl', `not a URL: ${JSON.stringify(trimmed)}`);
  }
  if (parsed.protocol !== 'https:') {
    throw new ConnectConfigError(
      'connectBaseUrl',
      `must be https: (got ${JSON.stringify(parsed.protocol)}); this is where the user signs in`,
    );
  }
  if (parsed.pathname !== '/' && parsed.pathname !== '') {
    throw new ConnectConfigError(
      'connectBaseUrl',
      `must be an origin with no path (got ${JSON.stringify(parsed.pathname)})`,
    );
  }
  return parsed.origin;
}

/**
 * Build the Connect login URL for a browser navigation.
 *
 * `return_url` is ABSOLUTE and built from the page's own origin, because Connect's
 * `return_url_guard` allowlists against the branding row's `identity.primary_url` and
 * needs a full URL to compare. It carries NO query of its own: the callback's only input
 * is the `exchange_code` Connect appends, and a return_url with its own parameters is
 * the shape most likely to trip an allowlist that compares prefixes.
 */
export function buildLoginUrl(config: ConnectConfig, pageOrigin: string): string {
  const base = normaliseConnectBaseUrl(config.connectBaseUrl);
  const returnUrl = new URL(config.callbackPath, ensureTrailingSlash(pageOrigin)).toString();
  const params = new URLSearchParams({ app: config.appKey, return_url: returnUrl });
  return `${base}/login?${params.toString()}`;
}

function ensureTrailingSlash(origin: string): string {
  return origin.endsWith('/') ? origin : `${origin}/`;
}

/** Where the browser should POST the bridge and GET `/me` + `/session/validate`. */
export function authApiUrl(config: ConnectConfig, endpoint: string): string {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const base = config.authBasePath.replace(/\/+$/, '');
  return `${base}${path}`;
}

/**
 * Pull the `exchange_code` out of a callback URL, or null.
 *
 * Returns null for anything that is not the callback path, so a stray `?exchange_code=`
 * on another route cannot drive a bridge POST. The code is single-use and IP-bound with
 * a 30s TTL, so a leaked one is low-value — but a bridge POST from an unexpected route
 * is still a request the server did not expect, and the narrow check costs nothing.
 */
export function exchangeCodeFrom(url: string, callbackPath: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.pathname.replace(/\/+$/, '') !== callbackPath.replace(/\/+$/, '')) return null;
  const code = parsed.searchParams.get('exchange_code');
  return code && code.trim() !== '' ? code : null;
}
